import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  Elements,
  ExpressCheckoutElement,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import type { CheckoutSession } from '@loctopus/ticketing-sdk';
import { ErrorSummary } from '@loctopus/ui';
import { useAppContext } from '../lib/AppContext';
import { useRouter } from '../router';
import { Button, Card, ErrorBanner, Spinner } from '../components/ui';
import { extractStripePublishableKey, getStripe } from '../lib/stripe';
import { toParsedError } from '../lib/errorDisplay';
import { formatMoney } from '../lib/format';

type LoadState =
  | { status: 'loading' }
  // Plain, already-specific application messages (no client secret / no Stripe key / Stripe.js
  // failed to load) - not an API error, so these stay as-is rather than forced through
  // parseApiError.
  | { status: 'info_error'; message: string }
  // A real thrown error from an SDK call - gets the full shared treatment (request ID, etc).
  | { status: 'api_error'; error: ReturnType<typeof toParsedError> }
  | { status: 'ready'; session: CheckoutSession; clientSecret: string; stripe: Stripe };

/**
 * `checkoutSessionId` doubles as the order id once payment succeeds - `packages/db/src/schema/orders.ts`
 * has one `orders` row per checkout attempt (status starts 'pending'), there is no separate
 * "checkout session" table/id in the schema. Confirm this with apps/api in Step C; if it turns
 * out to be a different id, this page just needs its post-payment navigate target updated.
 */
export function CheckoutPage({
  checkoutSessionId,
  accessToken,
}: {
  checkoutSessionId: string;
  accessToken?: string;
}) {
  const { client, config } = useAppContext();
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    client
      .getCheckoutSession(checkoutSessionId, { accessToken })
      .then(async (session) => {
        if (cancelled) return;
        const clientSecret = session.clientSecret;
        if (!clientSecret) {
          setState({
            status: 'info_error',
            message:
              'This checkout session has no payment client secret yet - it may have already been completed or expired.',
          });
          return;
        }
        const publishableKey =
          extractStripePublishableKey(session) ?? config.stripePublishableKeyFallback;
        if (!publishableKey) {
          setState({
            status: 'info_error',
            message:
              'No Stripe publishable key was provided by the checkout session or the runtime config - cannot mount the payment form.',
          });
          return;
        }
        const stripe = await getStripe(publishableKey);
        if (cancelled) return;
        if (!stripe) {
          setState({
            status: 'info_error',
            message: 'Stripe.js failed to load. Check your connection and try again.',
          });
          return;
        }
        setState({ status: 'ready', session, clientSecret, stripe });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({ status: 'api_error', error: toParsedError(err) });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [client, checkoutSessionId, accessToken, config]);

  if (state.status === 'loading') return <Spinner label="Loading payment…" />;
  if (state.status === 'info_error') return <ErrorBanner message={state.message} />;
  if (state.status === 'api_error') return <ErrorSummary error={state.error} />;

  return (
    <Elements
      stripe={state.stripe}
      options={{ clientSecret: state.clientSecret, appearance: { theme: 'stripe' } }}
    >
      <PaymentForm checkoutSessionId={checkoutSessionId} session={state.session} />
    </Elements>
  );
}

function PaymentForm({
  checkoutSessionId,
  session,
}: {
  checkoutSessionId: string;
  session: CheckoutSession;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { navigate } = useRouter();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Apple Pay / Google Pay / Link quick-pay row (Stripe's ExpressCheckoutElement) - shown ABOVE
  // the card form, not instead of it. `null` until Stripe tells us what's actually available in
  // this browser/device; the element itself renders nothing when no wallet is available, but the
  // surrounding card + "Or pay with card" divider would otherwise show as an empty box, so this
  // tracks that explicitly instead of guessing.
  const [hasExpressMethods, setHasExpressMethods] = useState<boolean | null>(null);

  const totals = useMemo(
    () => [
      { label: 'Subtotal', amount: session.subtotal },
      { label: 'Discount', amount: session.discount, hideIfZero: true },
      { label: 'Fees', amount: session.fees, hideIfZero: true },
    ],
    [session],
  );

  // Shared by both payment paths below: the regular card form's submit, and the express
  // checkout button's (Apple Pay / Google Pay / Link) onConfirm - both end up confirming the
  // SAME PaymentIntent against the SAME Elements instance, just triggered differently.
  async function confirmPayment() {
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);

    // Carry the SAME guest-buyer `accessToken` the checkout session was fetched with forward to
    // the order route (task #4's receipt-download feature needs it there - see router.tsx) -
    // through BOTH exit paths: the normal `redirect: 'if_required'` return below, and the
    // `return_url` for any provider flow (3DS, redirect-based wallets) that leaves this page and
    // comes back via a full browser navigation instead.
    const returnUrl = new URL(`/orders/${encodeURIComponent(checkoutSessionId)}`, window.location.origin);
    if (session.accessToken) returnUrl.searchParams.set('accessToken', session.accessToken);

    const { error: confirmError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: returnUrl.toString(),
      },
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(
        confirmError.message ??
          `Payment could not be confirmed (${confirmError.type}). Check your card details and try again.`,
      );
      setSubmitting(false);
      return;
    }

    navigate({ name: 'order', orderId: checkoutSessionId, accessToken: session.accessToken });
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    await confirmPayment();
  }

  return (
    <div>
      <h1 className="font-heading text-2xl font-semibold text-slate-800 mb-4">Payment</h1>
      <Card title="Order summary" className="mb-4">
        <ul className="text-sm text-slate-600 space-y-1">
          {totals.map(
            (row) =>
              (!row.hideIfZero || row.amount.amountMinor !== 0) && (
                <li key={row.label} className="flex justify-between">
                  <span>{row.label}</span>
                  <span>{formatMoney(row.amount)}</span>
                </li>
              ),
          )}
        </ul>
        <div className="flex justify-between font-semibold text-slate-800 mt-2 pt-2 border-t border-slate-100">
          <span>Total</span>
          <span>{formatMoney(session.total)}</span>
        </div>
      </Card>

      {hasExpressMethods !== false && (
        <Card className="mb-4">
          <ExpressCheckoutElement
            options={{ buttonType: { applePay: 'buy', googlePay: 'buy' } }}
            onReady={(event) =>
              setHasExpressMethods(
                !!event.availablePaymentMethods?.applePay ||
                  !!event.availablePaymentMethods?.googlePay,
              )
            }
            onConfirm={() => void confirmPayment()}
          />
          {hasExpressMethods && (
            <div className="flex items-center gap-3 mt-4 text-xs text-slate-400">
              <div className="flex-1 border-t border-slate-200" />
              Or pay with card
              <div className="flex-1 border-t border-slate-200" />
            </div>
          )}
        </Card>
      )}

      <Card>
        <form onSubmit={handleSubmit}>
          <PaymentElement />
          {error && (
            <div className="mt-3">
              <ErrorBanner message={error} />
            </div>
          )}
          <Button type="submit" size="lg" className="w-full mt-4" disabled={!stripe || submitting}>
            {submitting ? 'Processing…' : `Pay ${formatMoney(session.total)}`}
          </Button>
          <p className="mt-2 text-xs text-slate-500 text-center">
            Card details are entered directly into Stripe's secure payment form above - they never
            pass through our servers.
          </p>
        </form>
      </Card>
    </div>
  );
}
